import type { ReactNode } from "react";
import { useMemo } from "react";
import htmlToReact, {
	type HTMLReactParserOptions,
	type DOMNode,
	Element,
	domToReact,
} from "html-react-parser";
import { useIntl } from "react-intl";

import Obfuscate from "./Obfuscate";

export type UserTextProps = {
	dangerouslySetInnerHTML: {
		__html: string | TrustedHTML;
	};
};

export default function UserText({ dangerouslySetInnerHTML }: UserTextProps) {
	const htmlContent = dangerouslySetInnerHTML.__html;
	const content: ReactNode = useMemo(
		() => htmlToReact(String(htmlContent), htmlToReactOptions),
		[htmlContent],
	);
	return content;
}

const linkProtocolsToObfuscateArr = [
	"mailto",
	"tel",
	"sms",
	"facetime",
] as const;

type LinkProtocolToObfuscate = (typeof linkProtocolsToObfuscateArr)[number];

const linkProtocolsToObfuscate = new Set(linkProtocolsToObfuscateArr);

const htmlToReactOptions: HTMLReactParserOptions = {
	replace(node) {
		if (
			node instanceof Element &&
			node.name === "a" &&
			node.attribs.href !== undefined
		) {
			const hrefProtEnd = node.attribs.href.indexOf(":");
			if (0 <= hrefProtEnd) {
				const hrefProt = node.attribs.href.substring(0, hrefProtEnd);
				if (
					linkProtocolsToObfuscate.has(
						hrefProt as LinkProtocolToObfuscate,
					)
				) {
					return (
						<ObfuscatedLink
							href={node.attribs.href}
							protocol={hrefProt as LinkProtocolToObfuscate}
							contentDom={node.children as DOMNode[]}
						/>
					);
				}
			}
		}
	},
};

type ObfuscatedLinkProps = {
	href: string;
	protocol: LinkProtocolToObfuscate;
	contentDom: DOMNode[];
};

function ObfuscatedLink({ href, protocol, contentDom }: ObfuscatedLinkProps) {
	const intl = useIntl();
	const contentReact = useMemo(() => domToReact(contentDom), [contentDom]);

	return (
		<Obfuscate
			href={href}
			// @ts-expect-error broken prop types
			className="text-nowrap break-keep"
			title={intl.formatMessage({
				id: `obfuscatedLink.title.${protocol}`,
			})}
		>
			{contentReact}
		</Obfuscate>
	);
}
